import { ODSColumn, type ODSBreadcrumbMenuItemProps } from '@telekom-ods/react-ui-kit';
import { type ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';

import useOrgPermissions from '../../components/AppLayout/hooks/useOrgPermissions';
import BreadCrumb from '../../components/BreadCrumb';

import config from '@/config';
import { useUserData } from '@/redux/selectors';

interface BreadCrumbItem extends ODSBreadcrumbMenuItemProps {
  href: string;
  text: string;
}

const OrganizationBreadCrumb = (): ReactElement => {
  const intl = useIntl();
  const orgPermissions = useOrgPermissions();
  const { pathname } = useLocation();
  const roles = useUserData().roles ?? [];
  const isProductsTab = pathname.startsWith('/governance/products');
  const isUsersTab = pathname.startsWith('/governance/users');
  const currentPath = pathname.startsWith('/governance/organizations')
    ? pathname.split('/')[4]
    : pathname.split('/')[3];

  let sectionLink = '/governance/products';

  if (isUsersTab) {
    sectionLink = '/governance/users';
  } else if (!isProductsTab) {
    sectionLink = orgPermissions.permissions?.canSeeOrganizations
      ? '/governance/organizations'
      : `/governance/organizations/${orgPermissions.currentOrganization._id ?? ''}`;
  }

  let sectionText = 'organizations';

  if (isProductsTab) {
    sectionText = 'products';
  } else if (isUsersTab) {
    sectionText = 'users';
  }

  const hasGovernanceBreadcrumb = roles.some(role => config.elevatedPrivilegeRoles.includes(role));

  const organizationOnlyLink = orgPermissions.currentOrganization._id
    ? `/governance/organizations/${orgPermissions.currentOrganization._id}`
    : '/governance/organizations';

  const breadcrumbItems: (BreadCrumbItem | false)[] = [
    {
      href: '/',
      text: intl.formatMessage({ id: 'title' }),
    },
    hasGovernanceBreadcrumb && {
      href: '/governance/organizations',
      text: intl.formatMessage({ id: 'governance' }),
    },
    {
      href: hasGovernanceBreadcrumb ? sectionLink : organizationOnlyLink,
      text: intl.formatMessage({ id: hasGovernanceBreadcrumb ? sectionText : 'organization' }),
    },
    hasGovernanceBreadcrumb &&
      (!isProductsTab &&
      !isUsersTab &&
      orgPermissions.showOrganization() &&
      typeof orgPermissions.showOrganization() === 'string'
        ? {
            href: '#',
            text: orgPermissions.showOrganization() ?? '',
          }
        : false),
    hasGovernanceBreadcrumb && currentPath ? { href: '#', text: currentPath } : false,
  ];

  return (
    <ODSColumn
      data-testid='breadcrumb'
      style={{ marginTop: '0' }}
    >
      <BreadCrumb items={breadcrumbItems.filter(Boolean) as BreadCrumbItem[]} />
    </ODSColumn>
  );
};

export default OrganizationBreadCrumb;
